import React, { useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import { apiClient, type ApiError } from '../../utils/apiClient';
import { errorLine } from './labels';
import type { Rollout } from './types';

const BASE = '/api/backend/kiosk-release-rollouts';

export const RolloutActions = ({ rollout, onChanged }: { rollout: Rollout; onChanged: () => void }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<'pause' | 'cancel'>('pause');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reason, setReason] = useState('');
  const a = (key: string) => translate(`resources.kiosk-releases.actions.${key}`);

  const closeDialog = () => {
    setDialogOpen(false);
    setReason('');
  };

  const openDialog = (next: 'pause' | 'cancel') => {
    setMode(next);
    setDialogOpen(true);
  };

  const run = async (path: string, body?: Record<string, unknown>) => {
    setBusy(true);
    try {
      await apiClient.post(`${BASE}/${rollout.id}/${path}`, body);
      notify(a('done'), { type: 'success' });
      closeDialog();
      onChanged();
    } catch (error) {
      notify(errorLine(error as ApiError, translate), { type: 'error' });
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  if (rollout.status === 'cancelled' || rollout.status === 'completed') return null;
  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      {rollout.status === 'active' && <Button size="small" disabled={busy} onClick={() => openDialog('pause')} sx={{ textTransform: 'none' }}>{a('pause')}</Button>}
      {rollout.status === 'paused' && <Button size="small" disabled={busy} onClick={() => run('resume')} sx={{ textTransform: 'none' }}>{a('resume')}</Button>}
      <Button size="small" color="error" disabled={busy} onClick={() => openDialog('cancel')} sx={{ textTransform: 'none' }}>{a('cancel')}</Button>
      <Dialog open={dialogOpen} onClose={() => !busy && closeDialog()} maxWidth="xs" fullWidth>
        <DialogTitle>{mode === 'pause' ? a('pause') : a('cancel')}</DialogTitle>
        <DialogContent>
          {mode === 'pause' ? (
            <TextField autoFocus fullWidth label={a('pauseReason')} value={reason} onChange={(event) => setReason(event.target.value)} inputProps={{ 'data-testid': 'pause-reason', maxLength: 500 }} sx={{ mt: 1 }} />
          ) : (
            <Typography variant="body2">{a('cancelNote')}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={busy} sx={{ textTransform: 'none' }}>{translate('ra.action.cancel')}</Button>
          <Button
            variant="contained"
            color={mode === 'cancel' ? 'error' : 'primary'}
            disabled={busy || (mode === 'pause' && reason.trim().length < 3)}
            onClick={() => (mode === 'pause' ? run('pause', { reason: reason.trim() }) : run('cancel'))}
            sx={{ textTransform: 'none' }}
          >
            {mode === 'pause' ? a('pause') : a('cancel')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
